/* eslint-disable no-empty */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { showVoiceDetectedToast } from '../../../actions/toast';
import { proctoringUBALogger } from '../../../utils/trackingUtil';
import {
  validateAudioBlob,
  getAudioMetadata,
} from '../../../utils/audioProctoring';
import {
  getProctorCacheValue,
  incrementProctorCacheBasedOnKey,
} from '../../../utils/helperFunction';
import { processAudioProctorIncident } from '../../../actions/proctor';
import {
  SESSION_RECORDING_CAP_MS,
  MIN_AUDIO_DURATION_MS,
  MAX_CHUNK_DURATION_MS,
  USE_ONSPEECHEND_RECORDING,
  VAD_INCIDENT_COUNT_LOCALSTORAGE_KEY,
  VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
} from './constants';
import { isFirefoxBrowser, isSafariBrowser } from '../../../utils/utils';

const VAD = ({
  isEnabled,
  onVoiceDetected,
  test,
  assessmentId,
  token,
  isExtendTestInitialPhase,
  onMicrophoneError,
  offsetTime,
}) => {
  const dispatch = useDispatch();

  // Get VAD blocking problem type from Redux state
  const { vadBlockingProblemType } = useSelector(
    state => ({
      vadBlockingProblemType: state.proctor.vadBlockingProblemType,
    }),
    shallowEqual
  );

  // state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [voiceStartTime, setVoiceStartTime] = useState(null);
  const [isVADInitialised, setIsVADInitialised] = useState(false);
  const [voiceIncidentCount, setVoiceIncidentCount] = useState(() =>
    getProctorCacheValue(VAD_INCIDENT_COUNT_LOCALSTORAGE_KEY)
  );
  const [microphoneError, setMicrophoneError] = useState(null);
  const [microphoneMuteState, setMicrophoneMuteState] = useState({
    isMuted: false,
    muteStartTime: null,
    muteDuration: 0,
    muteCount: 0,
  });
  const [hasStoppedDueToIncidentCap, setHasStoppedDueToIncidentCap] = useState(
    false
  );

  // refs
  const vadRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const silenceRafRef = useRef(null);

  // algorithm refs
  const isSpeakingRef = useRef(false);
  const voiceStartRef = useRef(null); // marks when continuous speech began (based on sliding avg)
  const incidentCountRef = useRef(0); // mirror of voiceIncidentCount but in ref for closures
  const microphoneMuteStateRef = useRef(microphoneMuteState);
  // track last VAD high-level event and whether we already forced a restart for current speech
  const lastVadEventRef = useRef('idle'); // 'speech_start' | 'speech_end' | 'idle'
  const hasRestartedThisSpeechRef = useRef(false);
  // test activity guard (not expired and not submitted)
  const isTestActiveRef = useRef(true);
  // prevent concurrent re-inits
  const reinitInProgressRef = useRef(false);
  const safeReinitRef = useRef(() => {});

  // recording refs for handleFrame flow
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isRecordingRef = useRef(false);
  const recordingStartTimeRef = useRef(null);
  const sessionRecordingTimeRef = useRef(0);

  // forced-cut timer ref (used to enforce SESSION_RECORDING_CAP_MS)
  const forcedCutTimerRef = useRef(null);
  // per-segment timer to force cut individual segments at MAX_CLIP_DURATION_MS
  const segmentTimerRef = useRef(null);

  // helper to clear segment timer
  const clearSegmentTimer = useCallback(() => {
    if (segmentTimerRef.current) {
      clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
  }, []);

  // helper to schedule per-segment cut (max 10s or remaining session cap)
  const scheduleSegmentCut = useCallback(() => {
    clearSegmentTimer();
    const remainingCap =
      SESSION_RECORDING_CAP_MS - sessionRecordingTimeRef.current;
    if (remainingCap <= 0) return;
    const timeoutMs = Math.min(MAX_CHUNK_DURATION_MS, remainingCap); // Use 10s max chunk duration
    segmentTimerRef.current = setTimeout(() => {
      if (!isTestActiveRef.current) {
        proctoringUBALogger(
          'VAD:info',
          'Segment cut timer fired but test inactive - skipping'
        );
        return;
      }
      // If still speaking, pause to trigger onSpeechEnd, then restart (unless cap reached)
      if (
        isSpeakingRef.current &&
        vadRef.current &&
        typeof vadRef.current.pause === 'function'
      ) {
        try {
          vadRef.current.pause();
        } catch (e) {}
        setTimeout(() => {
          if (!isTestActiveRef.current) return;
          if (
            sessionRecordingTimeRef.current < SESSION_RECORDING_CAP_MS &&
            vadRef.current &&
            typeof vadRef.current.start === 'function'
          ) {
            try {
              vadRef.current.start();
            } catch (e) {}
          }
        }, 50);
      }
    }, timeoutMs);
  }, []);

  // keep ref in sync
  useEffect(() => {
    microphoneMuteStateRef.current = microphoneMuteState;
  }, [microphoneMuteState]);

  useEffect(() => {
    incidentCountRef.current = voiceIncidentCount;
  }, [voiceIncidentCount]);

  // keep test active ref in sync
  useEffect(() => {
    const active = !test?.is_expired && !test?.isTestSubmitted;
    isTestActiveRef.current = !!active;
  }, [test?.is_expired, test?.isTestSubmitted]);

  // helper to load session cap from localStorage
  const loadSessionCapFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(
        VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY
      );
      if (stored) {
        const remaining = parseInt(stored, 10);
        const used = SESSION_RECORDING_CAP_MS - remaining;
        sessionRecordingTimeRef.current = Math.max(0, used);
        proctoringUBALogger(
          'VAD:info',
          `Loaded session cap from localStorage: ${Math.round(
            remaining / 1000
          )}s remaining, ${Math.round(used / 1000)}s used`
        );
        return remaining;
      }

      // No stored value, initialize with current session recording time
      const currentUsed = sessionRecordingTimeRef.current;
      const remaining = SESSION_RECORDING_CAP_MS - currentUsed;
      localStorage.setItem(
        VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
        remaining.toString()
      );
      proctoringUBALogger(
        'VAD:info',
        `Initialized session cap in localStorage: ${Math.round(
          remaining / 1000
        )}s remaining, ${Math.round(currentUsed / 1000)}s used`
      );
      return remaining;
    } catch (storageError) {
      // Fallback: initialize with current value
      const currentUsed = sessionRecordingTimeRef.current;
      const remaining = SESSION_RECORDING_CAP_MS - currentUsed;
      try {
        localStorage.setItem(
          VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
          remaining.toString()
        );
      } catch (fallbackError) {
        // to do
      }
      return remaining;
    }
  }, []);

  // helper to save session cap to localStorage
  const saveSessionCapToStorage = useCallback(() => {
    try {
      const remaining =
        SESSION_RECORDING_CAP_MS - sessionRecordingTimeRef.current;
      localStorage.setItem(
        VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
        remaining.toString()
      );
      proctoringUBALogger(
        'VAD:info',
        `Session cap saved to localStorage: ${Math.round(
          remaining / 1000
        )}s remaining, ${Math.round(
          sessionRecordingTimeRef.current / 1000
        )}s used`
      );
    } catch (storageError) {
      // to do
    }
  }, []);

  // load session cap from localStorage on component mount
  useEffect(() => {
    if (isEnabled) {
      loadSessionCapFromStorage();
    }
  }, [isEnabled, loadSessionCapFromStorage]);

  // Initialize session cap when test starts (post test start)
  useEffect(() => {
    if (isEnabled && test && !test.is_expired && token && assessmentId) {
      // Initialize session cap with current value if not already set
      const stored = localStorage.getItem(
        VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY
      );
      if (!stored) {
        const remaining =
          SESSION_RECORDING_CAP_MS - sessionRecordingTimeRef.current;
        localStorage.setItem(
          VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
          remaining.toString()
        );
        proctoringUBALogger(
          'VAD:info',
          `Session cap initialized on test start: ${Math.round(
            remaining / 1000
          )}s remaining`
        );
      }
    }
  }, [isEnabled, test, token, assessmentId]);

  // Note: We don't clear session cap on unmount to maintain persistence across sessions

  // helper to clear forced-cut timer
  const clearForcedCutTimer = useCallback(() => {
    if (forcedCutTimerRef.current) {
      clearTimeout(forcedCutTimerRef.current);
      forcedCutTimerRef.current = null;
    }
  }, []);

  // helper to show voice detected toast
  const showVoiceDetectedToastHandler = useCallback(
    incidentCount => {
      if (!isTestActiveRef.current) {
        return;
      }
      dispatch(showVoiceDetectedToast('Voice Detected!', { incidentCount }));
      proctoringUBALogger(
        'VAD:audio proctoring info',
        `Voice detected toast shown for incident #${incidentCount}`
      );
    },
    [dispatch]
  );

  // helper to schedule forced-cut timer (remainingMs must be > 0)
  const scheduleForcedCut = useCallback(
    remainingMs => {
      clearForcedCutTimer();
      if (!remainingMs || remainingMs <= 0) return;

      proctoringUBALogger(
        'VAD:info',
        `Scheduling forced-cut in ${remainingMs}ms (session cap enforcement)`
      );

      forcedCutTimerRef.current = setTimeout(() => {
        if (!isTestActiveRef.current) {
          proctoringUBALogger(
            'VAD:info',
            'Forced cut timer fired but test inactive - skipping'
          );
          return;
        }
        forcedCutTimerRef.current = null;
        try {
          proctoringUBALogger(
            'VAD:info',
            'Forced cut triggered: pausing VAD to cause onSpeechEnd to run'
          );
          // Pause; because we'll set submitUserSpeechOnPause: true, pause -> triggers onSpeechEnd
          if (vadRef.current && typeof vadRef.current.pause === 'function') {
            try {
              vadRef.current.pause();
            } catch (e) {
              proctoringUBALogger(
                'VAD:warning',
                `Forced cut: vad.pause() failed: ${e.message}`
              );
            }
            // Immediately resume so we continue detecting next speech segments unless session cap reached
            setTimeout(() => {
              if (!isTestActiveRef.current) return;
              if (
                sessionRecordingTimeRef.current < SESSION_RECORDING_CAP_MS &&
                vadRef.current &&
                typeof vadRef.current.start === 'function'
              ) {
                try {
                  vadRef.current.start();
                } catch (e) {
                  proctoringUBALogger(
                    'VAD:warning',
                    `Forced cut: vad.start() failed: ${e.message}`
                  );
                }
              } else {
                // If cap already reached, stop VAD
                setHasStoppedDueToIncidentCap(true);
                try {
                  vadRef.current?.pause();
                } catch (e) {}
              }
            }, 50);
          }
        } catch (err) {
          proctoringUBALogger(
            'VAD:error',
            `Forced cut timer callback failed: ${err?.message || err}`
          );
        }
      }, remainingMs);
    },
    [clearForcedCutTimer]
  );

  const loadScript = src =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.body.appendChild(script);
    });

  // microphone permission/status check
  const checkMicrophoneStatus = useCallback(async () => {
    try {
      const permission = await navigator.permissions.query({
        name: 'microphone',
      });
      if (permission.state === 'denied') {
        throw new Error('Microphone permission denied');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) throw new Error('No microphone detected');

      const audioTrack = audioTracks[0];

      // Check initial mute state and update accordingly
      const initialMuted = audioTrack.muted || !audioTrack.enabled;
      if (initialMuted) {
        setMicrophoneMuteState(prev => ({
          ...prev,
          isMuted: true,
          muteStartTime: Date.now(),
          muteCount: prev.muteCount + 1,
        }));

        // Notify parent about initial mute state
        if (onVoiceDetected && isTestActiveRef.current) {
          try {
            onVoiceDetected({
              type: 'microphone_muted',
              reason: 'Initial state check - microphone muted',
              muteCount: 1,
              timestamp: new Date().toISOString(),
            });
          } catch (err) {
            // to do
          }
        }
      } else {
        setMicrophoneMuteState(prev => ({
          ...prev,
          isMuted: false,
          muteStartTime: null,
        }));
      }

      stream.getTracks().forEach(t => t.stop());
      setMicrophoneError(null);
      return { status: 'ok' };
    } catch (err) {
      setMicrophoneError(err.message);
      if (onMicrophoneError) onMicrophoneError(err.message);
      return { status: 'error', message: err.message };
    }
  }, [onMicrophoneError, onVoiceDetected]);

  // Convert AudioBuffer to WAV blob
  const audioBufferToWavBlob = async audioBuffer => {
    const { numberOfChannels, sampleRate, length } = audioBuffer;

    // Calculate data size
    const dataSize = length * numberOfChannels * 2; // 16-bit = 2 bytes per sample
    const fileSize = 36 + dataSize;

    // Create WAV file header
    const arrayBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(arrayBuffer);

    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i += 1) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    // RIFF header
    writeString(0, 'RIFF');
    view.setUint32(4, fileSize, true); // File size - 8
    writeString(8, 'WAVE');

    // Format chunk
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // Format chunk size
    view.setUint16(20, 1, true); // Audio format (PCM)
    view.setUint16(22, numberOfChannels, true); // Number of channels
    view.setUint32(24, sampleRate, true); // Sample rate
    view.setUint32(28, sampleRate * numberOfChannels * 2, true); // Byte rate
    view.setUint16(32, numberOfChannels * 2, true); // Block align
    view.setUint16(34, 16, true); // Bits per sample

    // Data chunk
    writeString(36, 'data');
    view.setUint32(40, dataSize, true); // Data size

    // Convert audio data to 16-bit PCM
    let offset = 44;
    for (let i = 0; i < length; i += 1) {
      for (let channel = 0; channel < numberOfChannels; channel += 1) {
        const sample = Math.max(
          -1,
          Math.min(1, audioBuffer.getChannelData(channel)[i])
        );
        // Convert to 16-bit signed integer
        const intSample = Math.round(sample * 32767);
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Helper function to chunk large audio data into smaller segments
  const chunkAudioData = (audioData, sampleRate = 16000) => {
    const durationMs = (audioData.length / sampleRate) * 1000;

    if (durationMs <= MAX_CHUNK_DURATION_MS) {
      return [audioData]; // No chunking needed
    }

    const samplesPerChunk = Math.floor(
      (MAX_CHUNK_DURATION_MS / 1000) * sampleRate
    );
    const chunks = [];

    for (let i = 0; i < audioData.length; i += samplesPerChunk) {
      const chunk = audioData.slice(i, i + samplesPerChunk);
      chunks.push(chunk);
    }

    return chunks;
  };

  // Convert Float32Array to audio blob
  const convertFloat32ArrayToBlob = async (
    float32Array,
    sampleRate = 16000
  ) => {
    try {
      // Validate input
      if (!float32Array || float32Array.length === 0) {
        return null;
      }

      // Create AudioContext for processing
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }

      // Resume AudioContext if suspended
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      // Create AudioBuffer from Float32Array
      const audioBuffer = audioContextRef.current.createBuffer(
        1, // mono channel
        float32Array.length,
        sampleRate
      );

      // Copy data to AudioBuffer
      audioBuffer.copyToChannel(float32Array, 0);

      // Convert AudioBuffer to WAV blob
      const wavBlob = await audioBufferToWavBlob(audioBuffer);

      proctoringUBALogger(
        'VAD:info',
        `Converted Float32Array to blob. Size: ${wavBlob.size} bytes, samples: ${float32Array.length}, sampleRate: ${sampleRate}`
      );
      return wavBlob;
    } catch (conversionError) {
      proctoringUBALogger(
        'VAD:error',
        `Float32Array conversion failed: ${conversionError.message}`
      );
      return null;
    }
  };

  const uploadAudioToS3 = async (
    audioBlob,
    incidentId,
    source = 'speech_end',
    duration = null
  ) => {
    try {
      if (!isTestActiveRef.current) {
        proctoringUBALogger(
          'VAD:info',
          `Skipping audio upload (source=${source}) - test inactive`
        );
        return false;
      }
      const validation = validateAudioBlob(audioBlob);
      if (!validation.isValid) {
        throw new Error(
          `Audio validation failed: ${validation.errors.join(', ')}`
        );
      }
      if (validation.warnings?.length) {
        proctoringUBALogger(
          'VAD:warning',
          `Audio validation warnings: ${validation.warnings.join(', ')}`
        );
      }
      const metadata = getAudioMetadata(audioBlob, duration);
      proctoringUBALogger(
        'VAD:info',
        `Processing audio incident ${incidentId}`,
        metadata
      );

      // Determine file type based on blob type
      const fileType = audioBlob.type.includes('wav') ? 'wav' : 'webm';

      // Dispatch Redux action to process audio incident (get signed URL then upload)
      dispatch(
        processAudioProctorIncident({
          incidentId,
          audioBlob,
          metadata: {
            incidentId,
            duration: metadata.duration,
            size: metadata.size,
            type: metadata.type,
            offset: offsetTime,
            source, // Mark source of recording
          },
          violationTimestamp: new Date().toISOString(),
          fileType,
        })
      );

      return true;
    } catch (err) {
      proctoringUBALogger(
        'VAD:error',
        `Audio processing failed for incident ${incidentId}: ${err.message}`
      );
      throw err;
    }
  };

  // initialize VAD (keeps your original MicVAD config with hooks, but uses handleFrame)
  const initializeVAD = useCallback(async () => {
    let ctx;
    let rafId;
    try {
      const myvad = await window.vad.MicVAD.new({
        model: 'v5',
        positiveSpeechThreshold: 0.4,
        negativeSpeechThreshold: 0.3,
        minSpeechFrames: 15,
        preSpeechPadFrames: 30,
        // Enable audio data collection
        collectAudioData: true,
        redemptionFrames: 24,
        frameSamples: 512,
        minSpeechMs: 400,
        redemptionMs: 500,

        // IMPORTANT: pause() will trigger onSpeechEnd when this option is true.
        submitUserSpeechOnPause: true,

        onSpeechRealStart: () => {
          // to do
        },
        onVADMisfire: () => {
          // Calculate duration from speech start to misfire
          const speechDuration = voiceStartRef.current
            ? Date.now() - voiceStartRef.current
            : 0;
          const speechDurationSeconds = Math.round(speechDuration / 1000);

          // Get current session usage
          const remainingCap =
            SESSION_RECORDING_CAP_MS - sessionRecordingTimeRef.current;
          const remainingCapSeconds = Math.round(remainingCap / 1000);

          proctoringUBALogger(
            'VAD:warning',
            `VAD misfire detected after ${speechDurationSeconds}s of speech - ${remainingCapSeconds}s remaining in session`
          );
        },
        // Do not rely on onFrameProcessed for segmentation to avoid continuous frames logic
        // and keep segmentation controlled by onSpeechStart/onSpeechEnd + timers
        // onFrameProcessed intentionally not used
        onSpeechStart: () => {
          if (!isTestActiveRef.current) return;
          setIsUserSpeaking(true);
          isSpeakingRef.current = true;

          // Track VAD events to avoid multiple pause/restart cycles
          const wasIdle = lastVadEventRef.current === 'idle';
          lastVadEventRef.current = 'speech_start';

          if (!voiceStartRef.current) {
            voiceStartRef.current = Date.now();
            setVoiceStartTime(voiceStartRef.current);
          }

          // schedule forced cut based on remaining session cap
          const remaining =
            SESSION_RECORDING_CAP_MS - sessionRecordingTimeRef.current;
          if (remaining <= 0) {
            proctoringUBALogger(
              'VAD:info',
              'Session cap already reached at speech start - pausing VAD'
            );
            setHasStoppedDueToIncidentCap(true);
            clearForcedCutTimer();
            try {
              vadRef.current?.pause();
            } catch (e) {}
            return;
          }

          // Only pause/restart on the FIRST speech start (transition from idle to speech)
          // Subsequent speech start events during continuous speech should not trigger pause/restart
          if (wasIdle && !hasRestartedThisSpeechRef.current) {
            hasRestartedThisSpeechRef.current = true;
            proctoringUBALogger(
              'VAD:info',
              'First speech start detected - pausing then restarting VAD for segment boundary'
            );

            // First, pause then start to ensure new segment boundaries
            try {
              if (
                vadRef.current &&
                typeof vadRef.current.pause === 'function'
              ) {
                vadRef.current.pause();
              }
            } catch (e) {}
            setTimeout(() => {
              try {
                if (
                  vadRef.current &&
                  typeof vadRef.current.start === 'function'
                ) {
                  vadRef.current.start();
                }
              } catch (e) {}
              // schedule forced cut for the remaining session cap and segment cut
              scheduleForcedCut(remaining);
              scheduleSegmentCut();
            }, 50);
          } else {
            // Subsequent speech start events - just schedule timers without pause/restart
            proctoringUBALogger(
              'VAD:info',
              'Subsequent speech start detected - scheduling timers only'
            );
            scheduleForcedCut(remaining);
            scheduleSegmentCut();
          }
        },
        onSpeechEnd: async data => {
          setIsUserSpeaking(false);
          isSpeakingRef.current = false;

          // Reset tracking variables for next speech segment
          lastVadEventRef.current = 'speech_end';
          hasRestartedThisSpeechRef.current = false;

          // clear forced cut timer (speech ended naturally)
          clearForcedCutTimer();
          clearSegmentTimer();

          // Reset to idle after a short delay to allow for next speech start to be treated as first
          setTimeout(() => {
            if (lastVadEventRef.current === 'speech_end') {
              lastVadEventRef.current = 'idle';
              proctoringUBALogger(
                'VAD:info',
                'VAD state reset to idle - ready for next speech segment'
              );
            }
          }, 1000); // 1 second delay

          // Only process onSpeechEnd recording if enabled
          if (
            USE_ONSPEECHEND_RECORDING &&
            !hasStoppedDueToIncidentCap &&
            isTestActiveRef.current
          ) {
            // Handle Float32Array data if available
            if (data && data instanceof Float32Array) {
              try {
                // Get the sample rate from the VAD data or use default
                const sampleRate = data.sampleRate || 16000; // VAD typically uses 16kHz

                // Chunk large audio data into smaller segments (max 10s each)
                const audioChunks = chunkAudioData(data, sampleRate);

                // Process each chunk separately (avoid await in loop)
                const processChunk = async (audioChunk, chunkIndex) => {
                  if (!isTestActiveRef.current) {
                    proctoringUBALogger(
                      'VAD:info',
                      'Skipping chunk processing - test inactive'
                    );
                    return false;
                  }
                  // Check if we've reached session cap before processing this chunk
                  if (
                    sessionRecordingTimeRef.current >= SESSION_RECORDING_CAP_MS
                  ) {
                    return false; // Stop processing
                  }

                  // Convert chunk to audio blob
                  const audioBlob = await convertFloat32ArrayToBlob(
                    audioChunk,
                    sampleRate
                  );

                  if (audioBlob) {
                    // Check session recording cap
                    if (
                      sessionRecordingTimeRef.current >=
                      SESSION_RECORDING_CAP_MS
                    ) {
                      const usedCapSeconds = Math.round(
                        sessionRecordingTimeRef.current / 1000
                      );
                      proctoringUBALogger(
                        'VAD:audio proctoring info',
                        `Session recording cap reached (${usedCapSeconds}s used), stopping onSpeechEnd processing`
                      );
                      setHasStoppedDueToIncidentCap(true);
                      try {
                        vadRef.current?.pause();
                      } catch (e) {}
                      return false;
                    }

                    // Compute estimated duration for this chunk and enforce min length (1s)
                    const estimatedDuration =
                      (audioChunk.length / sampleRate) * 1000;
                    if (estimatedDuration < MIN_AUDIO_DURATION_MS) {
                      const remainingCap =
                        SESSION_RECORDING_CAP_MS -
                        sessionRecordingTimeRef.current;
                      const remainingCapSeconds = Math.round(
                        remainingCap / 1000
                      );
                      const clipSeconds = Math.round(estimatedDuration / 1000);
                      proctoringUBALogger(
                        'VAD:warning',
                        `Discarding short clip < ${MIN_AUDIO_DURATION_MS}ms (${clipSeconds}s) - ${remainingCapSeconds}s remaining in session`
                      );
                      return false;
                    }

                    // Create incident for this speech segment
                    const newIncident = incrementProctorCacheBasedOnKey(
                      VAD_INCIDENT_COUNT_LOCALSTORAGE_KEY
                    );
                    incidentCountRef.current = newIncident;
                    setVoiceIncidentCount(newIncident);

                    // Update session recording time with this clip's duration
                    sessionRecordingTimeRef.current += estimatedDuration;
                    saveSessionCapToStorage(); // Save to localStorage

                    // Show toast only for incident < 3
                    if (newIncident < 3) {
                      showVoiceDetectedToastHandler(newIncident);
                    }

                    proctoringUBALogger(
                      'VAD:info',
                      `Speech end detected. Processing audio blob for incident #${newIncident}`
                    );

                    // Upload the audio blob
                    try {
                      await uploadAudioToS3(
                        audioBlob,
                        newIncident,
                        'speech_end',
                        estimatedDuration
                      );

                      // Calculate remaining session cap after this upload
                      const remainingCap =
                        SESSION_RECORDING_CAP_MS -
                        sessionRecordingTimeRef.current;
                      const remainingCapSeconds = Math.round(
                        remainingCap / 1000
                      );
                      const clipSeconds = Math.round(estimatedDuration / 1000);

                      proctoringUBALogger(
                        'VAD:audio proctoring info',
                        `Speech end audio uploaded for incident #${newIncident} (${clipSeconds}s clip, ${remainingCapSeconds}s remaining)`
                      );
                    } catch (uploadError) {
                      proctoringUBALogger(
                        'VAD:error',
                        `Upload failed for speech end incident #${newIncident}: ${uploadError.message}`
                      );
                    }
                  } else {
                    proctoringUBALogger(
                      'VAD:warning',
                      `Failed to convert chunk ${chunkIndex + 1}/${
                        audioChunks.length
                      } to audio blob`
                    );
                  }
                  return true; // Continue processing
                };

                // Process chunks sequentially using recursive approach to avoid await in loop
                const processChunksRecursively = async index => {
                  if (index >= audioChunks.length) return;

                  const shouldContinue = await processChunk(
                    audioChunks[index],
                    index
                  );
                  if (shouldContinue) {
                    await processChunksRecursively(index + 1);
                  }
                };

                await processChunksRecursively(0);
              } catch (conversionError) {
                proctoringUBALogger(
                  'VAD:error',
                  `Speech end audio processing failed: ${conversionError.message}`
                );
              }
            }
          }

          // small debounce to avoid flicker
          if (silenceRafRef.current)
            cancelAnimationFrame(silenceRafRef.current);
          silenceRafRef.current = requestAnimationFrame(() => {
            // If not speaking according to sliding window, clear
            if (!isSpeakingRef.current && voiceStartRef.current) {
              voiceStartRef.current = null;
              setVoiceStartTime(null);
            }
          });
        },
      });

      vadRef.current = myvad;
      setIsLoading(false);
      setIsVADInitialised(true);

      // Set up mute/unmute event listeners on myvad.stream if present (preserve existing behavior)
      if (myvad.stream) {
        try {
          const audioTracks = myvad.stream.getAudioTracks();
          if (audioTracks.length > 0) {
            const audioTrack = audioTracks[0];

            const handleMute = () => {
              setMicrophoneMuteState(prev => {
                if (!prev.isMuted) {
                  const now = Date.now();
                  if (onVoiceDetected && isTestActiveRef.current) {
                    try {
                      onVoiceDetected({
                        type: 'microphone_muted',
                        reason: 'Microphone is muted',
                        muteCount: prev.muteCount + 1,
                        timestamp: new Date().toISOString(),
                      });
                    } catch (err) {
                      // to do
                    }
                  }
                  return {
                    isMuted: true,
                    muteStartTime: now,
                    muteDuration: 0,
                    muteCount: prev.muteCount + 1,
                  };
                }
                return prev;
              });
            };

            const handleUnmute = () => {
              setMicrophoneMuteState(prev => {
                if (prev.isMuted) {
                  const now = Date.now();
                  const muteDuration = prev.muteStartTime
                    ? now - prev.muteStartTime
                    : 0;
                  if (onVoiceDetected && isTestActiveRef.current) {
                    try {
                      onVoiceDetected({
                        type: 'microphone_unmuted',
                        muteDuration,
                        muteDurationSeconds: (muteDuration / 1000).toFixed(2),
                        muteCount: prev.muteCount,
                        timestamp: new Date().toISOString(),
                      });
                    } catch (err) {
                      // to do
                    }
                  }
                  return {
                    isMuted: false,
                    muteStartTime: null,
                    muteDuration,
                    muteCount: prev.muteCount,
                  };
                }
                return prev;
              });
            };

            audioTrack.addEventListener('mute', handleMute);
            audioTrack.addEventListener('unmute', handleUnmute);

            // If the track ends (e.g., device permission toggled in OS), attempt to reinitialize
            const handleEnded = () => {
              proctoringUBALogger(
                'VAD:info',
                'Audio track ended - attempting VAD reinit'
              );
              if (typeof safeReinitRef.current === 'function') {
                try {
                  safeReinitRef.current();
                } catch (e) {}
              }
            };
            audioTrack.addEventListener('ended', handleEnded);

            // Safari fallback (silence detection) — preserved from your original code
            const isSafari = isSafariBrowser();
            if ((isFirefoxBrowser() || isSafari) && myvad.stream) {
              if (isFirefoxBrowser())
                audioTrack.addEventListener('ended', handleMute);
              ctx = new AudioContext();
              await ctx.audioWorklet.addModule(
                `${process.env.PUBLIC_URL}/mute-detection-worklet.js`
              );

              const src = ctx.createMediaStreamSource(
                new MediaStream([audioTrack])
              );
              const workletNode = new AudioWorkletNode(ctx, 'mute-detector');

              workletNode.port.onmessage = event => {
                if (event.data.type === 'mute') handleMute();
                else if (event.data.type === 'unmute') handleUnmute();
                else if (event.data.type === 'continuous-zero') handleMute();
              };

              src.connect(workletNode).connect(ctx.destination);
            }

            // Check initial state of the track
            const initialMuted = audioTrack.muted || !audioTrack.enabled;

            if (initialMuted && !microphoneMuteState.isMuted) {
              // If track is initially muted but our state doesn't reflect it, trigger mute
              handleMute();
            } else if (!initialMuted && microphoneMuteState.isMuted) {
              // If track is not muted but our state thinks it is, trigger unmute
              handleUnmute();
            }
          }
        } catch (e) {
          proctoringUBALogger(
            'VAD:warning',
            `Failed to setup audio track events: ${e.message}`
          );
        }
      }
      proctoringUBALogger('VAD:info', 'VAD initialized but not auto-started');
    } catch (err) {
      setError('Failed to initialize Voice Activity Detector');
      setIsLoading(false);
    }

    // Cleanup helper
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ctx) ctx.close();
    };
  }, [
    onVoiceDetected,
    scheduleForcedCut,
    clearForcedCutTimer,
    isFirefoxBrowser,
  ]);

  // Safely initialize or restart VAD when mic permission/device changes
  const safeInitOrRestartVAD = useCallback(async () => {
    // Validate current criteria
    if (
      !isTestActiveRef.current ||
      !isEnabled ||
      !token ||
      !assessmentId ||
      isExtendTestInitialPhase ||
      vadBlockingProblemType
    ) {
      proctoringUBALogger('VAD:info', 'Skip VAD reinit - criteria not met');
      return;
    }

    if (reinitInProgressRef.current) return;
    reinitInProgressRef.current = true;

    try {
      // Stop and cleanup existing VAD if present
      if (vadRef.current) {
        try {
          if (typeof vadRef.current.pause === 'function')
            vadRef.current.pause();
        } catch (e) {}
        try {
          if (typeof vadRef.current.destroy === 'function')
            vadRef.current.destroy();
          else if (typeof vadRef.current.stop === 'function')
            vadRef.current.stop();
        } catch (e) {}
        vadRef.current = null;
      }
      setIsListening(false);
      setIsVADInitialised(false);

      // Reinitialize VAD
      await initializeVAD();
      proctoringUBALogger(
        'VAD:info',
        'VAD reinitialized after permission/device change'
      );

      // Start if possible (effect will also handle this)
      try {
        if (vadRef.current && typeof vadRef.current.start === 'function') {
          vadRef.current.start();
        }
      } catch (e) {}
    } catch (err) {
      proctoringUBALogger(
        'VAD:warning',
        `VAD reinit failed: ${err?.message || err}`
      );
    } finally {
      reinitInProgressRef.current = false;
    }
  }, [
    assessmentId,
    initializeVAD,
    isEnabled,
    isExtendTestInitialPhase,
    token,
    vadBlockingProblemType,
  ]);
  // expose reinit function to imperative listeners defined earlier
  useEffect(() => {
    safeReinitRef.current = safeInitOrRestartVAD;
  }, [safeInitOrRestartVAD]);
  // Load scripts and initialize VAD (kept EXACTLY like your snippet)
  useEffect(() => {
    const loadScripts = async () => {
      try {
        if (window.vad && window.ort) {
          initializeVAD();
          return;
        }
        if (!window.ort) {
          await loadScript('https://assets.doselect.com/vad-web/onnx.js');
        }

        if (!window.vad) {
          await loadScript(
            'https://assets.doselect.com/vad-web/vad-web-bundle.min.js'
          );
        }

        setTimeout(() => {
          if (window.vad) {
            initializeVAD();
          } else {
            setError('Failed to load VAD library');
            setIsLoading(false);
          }
        }, 100);
      } catch (err) {
        setError('Failed to load required libraries');
        setIsLoading(false);
      }
    };

    loadScripts();

    // Listen for permission changes and device changes to reinit VAD without refresh
    let permissionStatus;
    (async () => {
      try {
        if (navigator?.permissions?.query) {
          permissionStatus = await navigator.permissions.query({
            name: 'microphone',
          });
          const handlePermChange = () => {
            // If permission becomes 'granted', try to reinit/start VAD
            if (permissionStatus.state === 'granted') {
              if (typeof safeReinitRef.current === 'function') {
                try {
                  safeReinitRef.current();
                } catch (e) {}
              }
            }
          };
          permissionStatus.onchange = handlePermChange;
        }
      } catch (e) {}
    })();

    const handleDeviceChange = async () => {
      // On device list changes (e.g., enabling mic from OS), re-check and reinit
      try {
        const devices = await navigator.mediaDevices?.enumerateDevices?.();
        const hasMic = Array.isArray(devices)
          ? devices.some(d => d.kind === 'audioinput')
          : true;
        if (hasMic) {
          if (typeof safeReinitRef.current === 'function') {
            try {
              safeReinitRef.current();
            } catch (e) {}
          }
        }
      } catch (e) {
        // best-effort
      }
    };
    try {
      navigator.mediaDevices?.addEventListener?.(
        'devicechange',
        handleDeviceChange
      );
    } catch (e) {}

    return () => {
      // Cleanup: stop any timers and audio nodes
      if (silenceRafRef.current) {
        cancelAnimationFrame(silenceRafRef.current);
        silenceRafRef.current = null;
      }

      // clear forced cut timer
      clearForcedCutTimer();

      // Cleanup recording resources
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {}
      }
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      isRecordingRef.current = false;
      recordingStartTimeRef.current = null;

      if (audioContextRef.current) {
        try {
          if (sourceNodeRef.current) {
            sourceNodeRef.current.disconnect();
            sourceNodeRef.current = null;
          }
          if (analyserRef.current) {
            analyserRef.current.disconnect();
            analyserRef.current = null;
          }
          audioContextRef.current.close();
        } catch (e) {}
        audioContextRef.current = null;
      }

      // remove listeners
      try {
        if (permissionStatus) permissionStatus.onchange = null;
        navigator.mediaDevices?.removeEventListener?.(
          'devicechange',
          handleDeviceChange
        );
      } catch (e) {}

      try {
        if (vadRef.current) {
          // keep destroy commented if you prefer, otherwise call destroy if available
          if (typeof vadRef.current.destroy === 'function') {
            vadRef.current.destroy();
          } else if (typeof vadRef.current.stop === 'function') {
            vadRef.current.stop();
          }
        }
      } catch (e) {
        // ignore
      }
    };
  }, [initializeVAD, clearForcedCutTimer]);

  // Start/pause VAD depending on test/token/flags (keeps your original gating)
  useEffect(() => {
    const shouldStart =
      !test.is_expired &&
      token &&
      isEnabled &&
      !test.isTestSubmitted &&
      !isExtendTestInitialPhase &&
      assessmentId &&
      !hasStoppedDueToIncidentCap &&
      !microphoneMuteState.isMuted && // Don't start VAD if microphone is muted
      !vadBlockingProblemType; // Don't start VAD if a blocking problem type is active
    if (shouldStart && isVADInitialised && vadRef.current && !isListening) {
      try {
        vadRef.current.start();
      } catch (e) {}
      setIsListening(true);
      proctoringUBALogger('VAD:info', 'VAD started - criteria met');
    } else if ((!shouldStart || !isEnabled) && vadRef.current && isListening) {
      try {
        vadRef.current.pause();
      } catch (e) {}
      setIsListening(false);
      proctoringUBALogger(
        'VAD:info',
        'VAD stopped - criteria not met or disabled'
      );
    }
  }, [
    test.is_expired,
    token,
    isEnabled,
    test.isTestSubmitted,
    isExtendTestInitialPhase,
    assessmentId,
    isListening,
    hasStoppedDueToIncidentCap,
    microphoneMuteState.isMuted,
    isVADInitialised,
    vadBlockingProblemType,
  ]);

  // Check microphone status on mount if enabled (preserve original)
  useEffect(() => {
    if (isEnabled) {
      checkMicrophoneStatus();
    }
  }, [isEnabled, checkMicrophoneStatus]);

  // Show/notify parent when speaking state changes
  useEffect(() => {
    if (onVoiceDetected && isUserSpeaking && isTestActiveRef.current) {
      try {
        const currentMuteDuration =
          microphoneMuteState.isMuted && microphoneMuteState.muteStartTime
            ? Date.now() - microphoneMuteState.muteStartTime
            : 0;

        onVoiceDetected({
          type: 'voice_detected',
          isSpeaking: isUserSpeaking,
          duration: voiceStartTime ? Date.now() - voiceStartTime : 0,
          incidentCount: voiceIncidentCount,
          microphoneMuteState: {
            ...microphoneMuteState,
            currentMuteDuration,
            currentMuteDurationSeconds: (currentMuteDuration / 1000).toFixed(2),
          },
        });
      } catch (err) {
        proctoringUBALogger(
          'VAD:error',
          `Parent voice detection callback error: ${err.message}`
        );
      }
    }
  }, [
    isUserSpeaking,
    voiceStartTime,
    voiceIncidentCount,
    microphoneMuteState,
    onVoiceDetected,
  ]);

  // Microphone error handling (preserve)
  useEffect(() => {
    if (!microphoneError) return;
    proctoringUBALogger(
      'VAD:error',
      `Microphone error detected: ${microphoneError}`
    );
    if (vadRef.current && isListening && !microphoneMuteState.isMuted) {
      try {
        vadRef.current.pause();
      } catch (e) {}
      // setMicrophoneMuteState(data => ({ ...data, isMuted: true }));
    }
    proctoringUBALogger(
      'VAD:info',
      'Microphone error detected - user needs to fix manually'
    );
  }, [microphoneError, isListening, microphoneMuteState.isMuted]);

  // clean up on unmount
  useEffect(() => {
    return () => {
      // Clean up timers
      clearForcedCutTimer();
      clearSegmentTimer();

      // Stop vad if active
      try {
        if (vadRef.current?.pause) vadRef.current.pause();
        if (vadRef.current?.destroy) vadRef.current.destroy();
      } catch (e) {}

      // Close AudioContext
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {}
        audioContextRef.current = null;
      }

      // Cancel raf if any
      if (silenceRafRef.current) {
        cancelAnimationFrame(silenceRafRef.current);
        silenceRafRef.current = null;
      }

      mediaRecorderRef.current = null;
      vadRef.current = null;
    };
  }, []);

  // Render: invisible by design but show simple messages for debugging
  if (isLoading) return <></>;
  if (error) return <></>;

  return <></>;
};

export default VAD;
